/**
 * 星海场景：时间螺线上的照片星、装饰粒子（平时是背景天幕）、螺线引导线、年份、中心的心。
 * 入场动画由 intro.ts 驱动：它通过 beginIntro / onFrame / endIntro 接管镜头和两层星点。
 * 全屏流体（fluid.ts）：指针划过、点星或空白处、入场爆炸都会推动它，最后一道画面处理按它扭曲画面。
 * 辉光在 post.ts，看照片时跟着变弱。星轨（trail.ts）沿螺线从第一张照片走到最新一张；流星（meteors.ts）偶尔划过。
 * 轮转（carousel.ts）：这一天的照片排成光环时，镜头和手势交给光环，星海变暗。
 * 缩略图面片池（photos.ts）：靠近时星显影成照片。照片、轮转卡片、选中星的圈都画在叠加层上，色调映射之后才叠上来。
 * 语义缩放（semantic.ts）：年份、纪念日射线、月份刻度、回忆标题按镜头远近显出来；Esc 一层一层往外退。
 * 按帧时间自动降级（quality.ts）：掉帧时依次降像素比、流体、星点和照片、辉光，顺畅了再升回来。
 * 改了在一起的日子（setSince）：每颗星沿着自己那天的射线滑到新螺线上。
 * 照片放进回收站（remove）：星原地荡开一圈涟漪后熄灭，序号和位置留着，恢复时在原处重新亮起来。
 */
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import {
  SPIRAL,
  StarPlacer,
  addDays,
  dayAnchor,
  parseLocalDate,
  dayRadius,
  daysBetween,
  hash01,
  localDateOf,
  toDayNumber,
  type LocalDate,
  type MemberDto,
  type MemberTint,
  type MemoryDto,
  type PhotoDto,
  type SpaceDto,
} from '@star-sea/shared';
import { Aurora } from './aurora.ts';
import { Carousel, type CarouselHooks } from './carousel.ts';
import { Cocoons, type CocoonInit } from './cocoons.ts';
import { ConstellationLines, StationRings, type RingInit } from './constellation.ts';
import { groupEvents, nebulaPatch, nebulaZoom, type NebulaPatch } from './events.ts';
import { Fluid } from './fluid.ts';
import { StarGrid } from './grid.ts';
import { heartSample, type HeartSample } from './heart.ts';
import { Meteors } from './meteors.ts';
import { EventNebula } from './nebula.ts';
import { arcOf, type TrailPath } from './path.ts';
import { PhotoPool } from './photos.ts';
import { pickStar } from './pick.ts';
import { ToneMapPass } from './post.ts';
import { QUALITY_LEVELS, QualityGovernor } from './quality.ts';
import { roamAt, roamCamera, roamTimeline, type RoamTimeline } from './roam.ts';
import { DAY_LEVEL, SemanticLayer, TITLE_MIN, YEAR_VIEW, type DayInfo } from './semantic.ts';
import { StarField } from './stars.ts';
import { StarTrail } from './trail.ts';

export interface GalaxyData {
  space: SpaceDto;
  members: MemberDto[];
  memories: MemoryDto[];
  photos: PhotoDto[];
}

const TINT: Record<MemberTint, THREE.Color> = { gold: new THREE.Color('#ffd79a'), rose: new THREE.Color('#ff9fbf') };
const FOV = 50;
const ELEVATION = THREE.MathUtils.degToRad(50);
/**
 * 俯仰限位（2026-10-02 改造需求 4.2）：转镜头时仰角不低于 35°，不会翻到盘面底下迷失方向。
 * 需求原文是 −15° ～ +40°，按相对默认俯视角（50°）理解：最低 35°，最高正上方。
 */
const MAX_POLAR = THREE.MathUtils.degToRad(90 - 35);
/** 屏幕左右边缘这么宽（CSS 像素）留给系统的返回手势：手指从这里按下不转镜头、不点星 */
const EDGE = 20;
/** 装饰粒子：入场时和照片星一起拼成爱心，爆炸后散到远处，成为背景天幕 */
const DUST_DESKTOP = 9000;
const DUST_MOBILE = 5000;
/** 指针划过时推动流体的力度，同原型 */
const FORCE = 5200;
/** 改了在一起的日子后星滑到新位置：离中心近的先动，最外圈晚这么多秒，像一圈波纹往外推 */
const MOVE_SPREAD = 0.7;
/** 星落定以后，螺线、年份、星轨、照片花这么久淡入（秒） */
const MOVE_FADE = 0.6;
/** 漫游里 seek 跳站：镜头从现在的位置往新站混这么久（开场那一次混合用 start 段的时长，见 roam.ts） */
const ROAM_SEEK_BLEND = 0.6;
/** 放进回收站的星：出生时间设成很远的将来，着色器不画、点选不中（stars.ts、pick.ts） */
const GONE = 1e9;

/** 星的大小：回忆越心动越大，每颗再随机大小一点 */
function starSize(shine: number, seed: number): number {
  return (0.55 + 0.11 * shine) * (0.8 + 0.4 * seed);
}

function easeInOut(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** 照片主色往往偏灰，做成星要更亮更饱和；再带一点添加人的颜色 */
function starColor(hex: string, tint: MemberTint | undefined, out: THREE.Color): THREE.Color {
  const hsl = { h: 0, s: 0, l: 0 };
  out.set(hex).getHSL(hsl, THREE.SRGBColorSpace);
  out.setHSL(hsl.h, Math.min(0.95, 0.3 + hsl.s * 0.9), THREE.MathUtils.clamp(0.6 + (hsl.l - 0.5) * 0.35, 0.5, 0.78), THREE.SRGBColorSpace);
  if (tint) out.lerp(TINT[tint], 0.12);
  return out;
}

/**
 * 画好的 canvas 按像素数据（ImageData）上传，不直接把 canvas 元素交给 WebGL：
 * Safari 18 上传 canvas 元素、又要求不预乘透明度时，接近不透明的地方会混进零星的坏点，
 * 中心光晕里那团青色小点就是这么来的；Chrome 没有这个问题。
 */
function canvasTexture(c: HTMLCanvasElement): THREE.Texture {
  const t = new THREE.Texture(c.getContext('2d')!.getImageData(0, 0, c.width, c.height));
  t.colorSpace = THREE.SRGBColorSpace;
  t.needsUpdate = true;
  return t;
}

function glowTexture(inner: string, outer: string): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  grad.addColorStop(0, inner);
  grad.addColorStop(0.25, outer);
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  return canvasTexture(c);
}

function ringTexture(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  g.strokeStyle = 'rgba(255, 236, 210, 0.95)';
  g.lineWidth = 5;
  g.beginPath();
  g.arc(64, 64, 52, 0, Math.PI * 2);
  g.stroke();
  g.strokeStyle = 'rgba(255, 236, 210, 0.25)';
  g.lineWidth = 12;
  g.stroke();
  return canvasTexture(c);
}

interface Flight {
  fromPos: THREE.Vector3;
  fromTarget: THREE.Vector3;
  toPos: THREE.Vector3;
  toTarget: THREE.Vector3;
  t0: number;
  dur: number;
}

export interface CameraPose {
  pos: THREE.Vector3;
  target: THREE.Vector3;
}

/** 时光漫游的回调（design §11.11）：界面下一段用它放卡片、驱动控制条 */
export interface RoamHooks {
  /** 到第几站（seek 跳到的也算） */
  onArrive(stop: number): void;
  /** 离开第几站（seek 从站上跳走也算） */
  onDepart(stop: number): void;
  /** 每帧报时间轴上的位置（暂停时也报）：界面刷进度线用；不占 onFrame，入场动画在用它 */
  onTick(t: number, total: number): void;
  /** 漫游停了，不管什么原因：走完了是 true，被退出或被别的操作打断是 false */
  onStop(finished: boolean): void;
}

/** 漫游状态：路线、时间轴定下就不变——漫游中照片增删、星轨重建，都照旧走完 */
interface Roam {
  /** 这次漫游走的路线（开始那一刻星轨的 path） */
  path: TrailPath;
  tl: RoamTimeline;
  /** 只显影停靠站那几天的照片：飞向下一站时，那一站的照片在前方先亮出来，别的日子只是星 */
  days: Set<LocalDate>;
  /** 这一趟是星座里的漫游：沿金线逐站直飞、没有收尾段、彗星藏着，结束时回装得下星座的位置 */
  constel: boolean;
  hooks: RoamHooks;
  t: number;
  /** 快进时 2.5，平时 1 */
  speed: number;
  paused: boolean;
  /** 上一帧漫游在第几段：到站、离站回调按它 diff */
  leg: number;
  /** 现在停着的或者最近到过的那站，还没到第一站是 -1 */
  stop: number;
  /** 从「开始/seek 前的镜头」往漫游镜头混：dur 秒，easeInOut；减少动态效果时不混，直接切 */
  blend: { pos: THREE.Vector3; target: THREE.Vector3; t: number; dur: number } | null;
}

export interface GalaxyStats {
  fps: number;
  frameMs: number;
  calls: number;
  stars: number;
}

export class Galaxy {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(FOV, 1, 0.1, 6000);
  readonly controls: OrbitControls;
  readonly stars = new StarField(2048);
  readonly dust: StarField;
  /** 全屏流体；入场爆炸的冲击波也推它 */
  readonly fluid: Fluid;
  private readonly trail = new StarTrail();
  private readonly meteors = new Meteors();
  /** 时间胶囊的星茧（cocoons.ts，ADR-0011）；capsules 是现在的胶囊（改了在一起的日子时按它重新摆） */
  private readonly cocoons = new Cocoons();
  private capsules: { id: string; date: LocalDate; phase: number }[] = [];
  /** 入场暗号的足迹（Ta 看过的那句，notes.ts）：和星茧画在同一层 */
  private footprints: { id: string; date: LocalDate }[] = [];
  /** 你们俩的生日（semantic.ts 的生日射线）：label 是「小满 的生日」 */
  private birthdays: { label: string; monthDay: string }[] = [];
  /** 主题星座（design §11.10）：连线；constelDates 是正在看的那个星座的各站，null 是没在看 */
  private readonly constel = new ConstellationLines();
  /** 每一站的细金环（第二期） */
  private readonly rings = new StationRings();
  private constelDates: LocalDate[] | null = null;
  /** 正在看的星座的名字、星座里现在看的那一站；语义缩放里的站名要不要清（退出时等线淡完再清） */
  private constelName = '';
  private currentStation: LocalDate | null = null;
  private constelLabels = false;
  /** 事件星云（design §11.11）：连着有照片的日子外面罩的淡雾；nebulaDirty 跟着 daysDirty 的节流一起重算 */
  private readonly nebula = new EventNebula();
  private nebulaDirty = false;
  /** 星茧、足迹本来的可见度（applyScenery 算）；星座里再淡到六成 */
  private marksK = 1;
  /** 线的可见度、不在主题里的星压暗了多少（都平滑地跟过去）；线画出来了多少；虚线流了多远 */
  private constelK = 0;
  /** 漫游平滑过渡的系数：语义缩放的标签按它淡到三成——停站时「去大理 · 30 张」叠在站上很乱，卡片上已经有日期和标题 */
  private roamK = 0;
  private dimK = 0;
  private constelReveal = 1;
  private constelFlow = 0;
  /** 进入星座前的镜头：退出时飞回去 */
  private constelPose: CameraPose | null = null;
  /** 点到了一颗时间胶囊 */
  onCapsule: ((id: string) => void) | null = null;
  /** 点了一个足迹 */
  onFootprint: ((id: string) => void) | null = null;
  /** 纪念日入场的极光（ritual.ts）；强度和升起的程度由入场动画每帧给 */
  private readonly aurora = new Aurora();
  private auroraK = 0;
  private auroraRise = 0;
  /** 日常、纪念日入场：照片星从中心的光点里绽开（bloom） */
  private blooming: { t0: number; dur: number } | null = null;
  /** 中心光晕亮一下（绽开、Ta 赴约的回响），每帧衰减；coreOpacity 是不亮的时候的不透明度 */
  private coreFlare = 0;
  private coreOpacity = 0.7;
  /** 防借看（setDisguise）：只留星点和天幕，照片、标签、螺线、星轨都藏起来，点星也不开 */
  private disguised = false;
  /** 叠加层：照片面片、轮转卡片、选中星的圈；不参与辉光和色调映射 */
  private readonly overlay = new THREE.Scene();
  private readonly grid = new StarGrid();
  /** 最大的星有多大：点选时大星的点中半径按它算 */
  private maxSize = 0;
  /** 缩略图面片池 */
  readonly pool: PhotoPool;
  /** 轮转光环；progress 上次用到的值（变了才改螺线、年份的可见度） */
  private readonly carousel: Carousel;
  private ringShown = -1;
  private readonly ndcV = new THREE.Vector2();
  /** 星轨开着没有（顶栏的开关）；trailK 是它当前的可见度，平滑地跟过去 */
  private trailOn = true;
  private trailK = 0;
  /** 星轨现在覆盖的照片日期；新照片超出这个范围才重建 */
  private trailRange: [string, string] | null = null;
  private readonly post = new ToneMapPass();
  readonly stats: GalaxyStats = { fps: 0, frameMs: 16.7, calls: 0, stars: 0 };
  photos: PhotoDto[] = [];
  onSelect: ((index: number) => void) | null = null;
  /** 每帧渲染前调用（入场动画用），参数是这一帧的时长和累计时间 */
  onFrame: ((dt: number, time: number) => void) | null = null;
  /** 入场期间镜头由入场动画控制，也不能点选 */
  private introActive = false;

  private space: SpaceDto | null = null;
  private placer: StarPlacer | null = null;
  private readonly index = new Map<string, number>();
  private readonly byDate = new Map<string, number[]>();
  private tints = new Map<string, MemberTint>();
  private shine = new Map<string, number>();
  private guide: THREE.Line | null = null;
  private guideDirty = false;
  /** 螺线、年份、中心光晕的可见度；入场期间为 0 */
  private scenery = 1;
  /** 0 是看星海，1 是在看照片：辉光跟着变弱、阈值变高，免得抢照片的戏（同原型的聚焦模式） */
  private bloomK = 0;
  /**
   * 辉光的强度、阈值、半径：[看星海, 看照片, 入场]。
   * 原型三者都是 0.9 / 0.2 / 0.55 起步，但这里的照片星比原型的星点亮得多（一天几百张会叠成一团），
   * 照搬的话星团会晕成一片；所以星海里只让真正亮的地方发光，入场的爱心用原型那样低的阈值。
   * 入场到螺线淡入时，从入场这组过渡回星海这组。
   */
  private readonly bloomLook = { strength: [0.7, 0.4, 0.9], threshold: [1.2, 1.8, 0.35], radius: [0.5, 0.4, 0.55] };
  private readonly core: THREE.Sprite;
  private readonly ring: THREE.Sprite;
  /** 年份、月份、纪念日、回忆标题（semantic.ts）；daysDirty 时重算哪些日子显标题 */
  private readonly semantic: SemanticLayer;
  private titles = new Map<string, string>();
  private daysDirty = false;
  private daysAt = 0;
  /** 星在滑向新螺线（setSince），到这个时刻全部落定；moveShown 是上次用到的淡入程度 */
  private moveEnd = -100;
  private moveShown = 1;
  /** 放进了回收站的星（序号）：不画、点不中、不显影，也不算在这一天的照片里 */
  private readonly gone = new Set<number>();
  /** 张数变了的日子：下一帧把这一天每颗星的 aCrowd 改成新的张数（stars.ts 按它压亮度） */
  private readonly crowdDirty = new Set<string>();
  /** 画质档位（quality.ts）：像素比上限、辉光开不开 */
  private readonly governor: QualityGovernor;
  private readonly onQuality: ((level: number) => void) | undefined;
  private readonly low: boolean;
  private dprCap = 2;
  private bloomOn = true;
  private selected = -1;
  /** 画面中心竖直移的比例：正数上移（手机上打开底部面板时让星留在面板上方），负数下移（漫游时星团让到卡片下面） */
  private shift = 0;
  private shiftTarget = 0;
  private flight: Flight | null = null;
  /** 时光漫游中（design §11.11）：镜头由漫游时间轴接管，彗星被推着走，只显影停靠站那天的照片 */
  private roam: Roam | null = null;
  private readonly roamCam: CameraPose = { pos: new THREE.Vector3(), target: new THREE.Vector3() };
  private time = 0;
  private last = performance.now();
  private outer = SPIRAL.r0;
  private readonly container: HTMLElement;
  private readonly labelLayer: HTMLElement;
  private readonly reducedMotion: boolean;
  /** 流体推力的倍数；减少动态效果时降到三成（同原型） */
  private readonly distort: number;
  private readonly pointer = { x: 0, y: 0, has: false };
  private readonly dyeColor = new THREE.Color();
  private readonly tmp = new THREE.Vector3();
  private readonly tmpColor = new THREE.Color();
  private readonly heart: HeartSample = { x: 0, y: 0, z: 0, kind: 0, bx: 0, by: 0, bz: 0 };
  private down: { x: number; y: number; t: number } | null = null;

  /**
   * thumbs：缩略图从哪来（压测模式没有照片，用主色渐变代替；token 给 Worker 取图用）。
   * quality：从哪一档画质开始、是不是固定不动、换档时通知谁（main.ts 记住档位）。
   */
  constructor(
    container: HTMLElement,
    labelLayer: HTMLElement,
    opts: { reducedMotion: boolean; thumbs: { fake: boolean; token: string | null }; quality: { start: number; fixed: boolean; onChange?: (level: number) => void } },
  ) {
    this.container = container;
    this.labelLayer = labelLayer;
    this.reducedMotion = opts.reducedMotion;
    this.renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'high-performance' });
    this.renderer.setClearColor('#04050c');
    // 一帧要画两次（场景、色调映射），绘制次数自己清零
    this.renderer.info.autoReset = false;
    container.appendChild(this.renderer.domElement);
    this.distort = this.reducedMotion ? 0.3 : 1;
    // 手机和小屏用低一档的分辨率（同原型）
    const low = (this.low = matchMedia('(pointer: coarse)').matches || Math.min(innerWidth, innerHeight) < 560);
    this.fluid = new Fluid(this.renderer, { simRes: low ? 96 : 128, dyeRes: low ? 192 : 320 });
    this.semantic = new SemanticLayer(labelLayer);
    this.scene.add(this.semantic.group);
    this.governor = new QualityGovernor({ start: opts.quality.start, fixed: opts.quality.fixed, now: performance.now() });
    this.onQuality = opts.quality.onChange;
    // 光环最多 12 格，纹理再多留几张（ADR-0004 的近档：桌面 64 槽、手机 24 槽，够用）
    this.carousel = new Carousel(this.overlay, { maxSlots: 12, spare: low ? 2 : 4, reduced: this.reducedMotion });
    // ADR-0004：中档 128px 桌面 1024 层、手机 384 层；近档 512px 桌面 64 层、手机 24 层
    this.pool = new PhotoPool(this.renderer, this.grid, { low, ...opts.thumbs });
    this.overlay.add(this.pool.mesh);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.07;
    this.controls.rotateSpeed = 0.55;
    this.controls.zoomSpeed = 0.9;
    this.controls.zoomToCursor = true;
    this.controls.minDistance = 1.2;
    this.controls.autoRotate = !this.reducedMotion;
    this.controls.autoRotateSpeed = 0.18;
    this.controls.addEventListener('start', () => {
      this.controls.autoRotate = false;
      this.flight = null;
    });

    this.stars.uniforms.uCluster.value = SPIRAL.cluster;
    this.stars.uniforms.uTwinkle.value = this.reducedMotion ? 0 : 1;
    if (this.reducedMotion) this.stars.uniforms.uMove.value = 0.01;
    // 照片星按日期先后落定，留出更大的先后差
    this.stars.uniforms.uStagger.value = 0.4;
    this.stars.mesh.renderOrder = 1;
    this.dust = this.makeDust(matchMedia('(pointer: coarse)').matches ? DUST_MOBILE : DUST_DESKTOP);
    this.dust.uniforms.uTwinkle.value = this.reducedMotion ? 0 : 1;
    this.dust.uniforms.uRestGain.value = 3;
    this.scene.add(this.dust.mesh);
    this.scene.add(this.stars.mesh);
    // 减少动态效果时彗星不走，只留一条静止的星轨
    this.trail.uniforms.uComet.value = this.reducedMotion ? 0 : 1;
    this.scene.add(this.trail.points, this.meteors.group, this.aurora.mesh, this.cocoons.mesh, this.constel.group, this.rings.mesh, this.nebula.mesh);

    this.core = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: glowTexture('rgba(255,235,220,1)', 'rgba(255,140,175,0.35)'), blending: THREE.AdditiveBlending, depthWrite: false, transparent: true, opacity: 0.7 }),
    );
    this.core.scale.setScalar(SPIRAL.r0 * 1.1);
    this.scene.add(this.core);

    // 选中星的圈画在叠加层上，盖在这颗星的照片上面；只加颜色、不写透明度，叠上去还是发光的样子
    this.ring = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: ringTexture(),
        sizeAttenuation: false,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        blending: THREE.CustomBlending,
        blendSrc: THREE.SrcAlphaFactor,
        blendDst: THREE.OneFactor,
        blendSrcAlpha: THREE.ZeroFactor,
        blendDstAlpha: THREE.OneFactor,
      }),
    );
    this.ring.visible = false;
    this.ring.renderOrder = 10;
    this.overlay.add(this.ring);

    const el = this.renderer.domElement;
    // 边缘留给系统：在外层容器的捕获阶段拦下，画布（镜头控制、点选、光环）都收不到这次按下
    container.addEventListener(
      'pointerdown',
      (e) => {
        if (e.pointerType === 'mouse') return;
        const r = container.getBoundingClientRect();
        if (e.clientX - r.left < EDGE || r.right - e.clientX < EDGE) e.stopPropagation();
      },
      true,
    );
    el.addEventListener('pointerdown', (e) => {
      this.down = { x: e.clientX, y: e.clientY, t: performance.now() };
      // 光环里拖动是转光环（镜头不动）
      if (this.carousel.active) {
        this.carousel.grab(e.clientX);
        el.setPointerCapture?.(e.pointerId);
      }
    });
    el.addEventListener('pointerup', (e) => {
      const d = this.down;
      this.down = null;
      this.carousel.release();
      // 漫游、入场时镜头被接管：点按不选星、不点星茧（漫游里点按是暂停，下一段做）
      if (this.introActive || this.roam || !d || Math.hypot(e.clientX - d.x, e.clientY - d.y) > 6 || performance.now() - d.t > 600) return;
      // 星还在滑向新螺线：点到的位置和画出来的对不上，这两秒先不理
      if (this.time < this.moveEnd) return;
      if (this.carousel.active) return this.tapCarousel(e);
      // 时间胶囊比照片星大、也更少：先看是不是点到了星茧
      if (!this.disguised && this.scenery > 0.5) {
        const r = el.getBoundingClientRect(), cap = this.cocoons.pick(this.camera, e.clientX - r.left, e.clientY - r.top, r.width, r.height);
        if (cap) {
          this.fluid.burst((e.clientX - r.left) / r.width, 1 - (e.clientY - r.top) / r.height, 0.6 * this.distort);
          if (this.footprints.some((f) => f.id === cap)) this.onFootprint?.(cap);
          else this.onCapsule?.(cap);
          return;
        }
      }
      // 伪装着：点哪里都只是一圈涟漪，不开照片
      const i = this.disguised ? -1 : this.pickAt(e.clientX, e.clientY);
      // 点到空白处：那里荡开一圈涟漪；点到星的涟漪在 select 里
      if (i < 0) {
        const r = el.getBoundingClientRect();
        this.fluid.burst((e.clientX - r.left) / r.width, 1 - (e.clientY - r.top) / r.height, 0.8 * this.distort);
      }
      this.select(i);
    });
    el.addEventListener('pointermove', (e) => {
      this.stir(e);
      if (!this.carousel.active) return;
      this.carousel.drag(e.clientX);
      if (!this.down) el.style.cursor = this.carousel.hover(this.ndc(e), this.camera) ? 'pointer' : '';
    });
    el.addEventListener('pointerleave', () => {
      this.pointer.has = false;
      this.carousel.hover(null, this.camera);
    });
    el.addEventListener('pointercancel', () => {
      this.down = null;
      this.carousel.release();
    });
    el.addEventListener(
      'wheel',
      (e) => {
        if (!this.carousel.active) return;
        e.preventDefault();
        this.carousel.wheel(Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY);
      },
      { passive: false },
    );

    new ResizeObserver(() => this.resize()).observe(container);
    this.applyQuality(this.governor.level);
    this.renderer.setAnimationLoop(() => this.frame());
  }

  /** 画质档位，0 最好（quality.ts） */
  get qualityLevel(): number {
    return this.governor.level;
  }

  /** 换到某一档画质：像素比、流体、辉光、装饰粒子、显影照片的上限 */
  private applyQuality(level: number): void {
    const q = QUALITY_LEVELS[level]!, u = this.post.uniforms;
    this.dprCap = q.dpr;
    this.bloomOn = q.bloom;
    this.fluid.enabled = q.fluid !== 'off';
    if (q.fluid === 'full') {
      this.fluid.setResolution(this.low ? 96 : 128, this.low ? 192 : 320);
      this.fluid.idleAfter = 10;
    } else if (q.fluid === 'lite') {
      // 低分辨率，而且只在推完之后 2 秒内算（design §10：只在交互时跑）
      this.fluid.setResolution(64, 128);
      this.fluid.idleAfter = 2;
    }
    // 流体关掉时最后一道画面处理也不扭曲、不染色
    u.uDistort.value = this.fluid.enabled ? 0.0002 : 0;
    u.uDye.value = this.fluid.enabled ? 0.32 : 0;
    this.dust.setLimit(Math.round(this.dust.count * q.dust));
    this.pool.setCap(q.photos);
    this.resize();
  }

  /** 装饰粒子：平时待在远处的球壳上当背景天幕，入场时拼成爱心。位置和颜色只由序号决定 */
  private makeDust(n: number): StarField {
    const dust = new StarField(n), c = new THREE.Color();
    for (let i = 0; i < n; i++) {
      const id = `sky${i}`;
      const u = hash01(id, 1) * 2 - 1, a = hash01(id, 2) * Math.PI * 2, r = 1800 + hash01(id, 3) * 800, s = Math.sqrt(1 - u * u);
      const k = hash01(id, 4);
      c.setHSL(k < 0.12 ? 0.94 : k < 0.55 ? 0.6 : 0.09, k < 0.12 ? 0.6 : 0.4, 0.8, THREE.SRGBColorSpace);
      const h = heartSample(i + 1, this.heart);
      dust.push({
        x: Math.cos(a) * s * r,
        y: u * r,
        z: Math.sin(a) * s * r,
        color: c,
        size: (Math.pow(hash01(id, 5), 3.2) * 2.6 + 0.8) * 1.2,
        born: -100,
        seed: hash01(id, 6),
        heart: { x: h.x, y: h.y, z: h.z, kind: h.kind },
        burst: { x: h.bx, y: h.by, z: h.bz },
        order: hash01(id, 7),
      });
    }
    return dust;
  }

  /** 指针划过：顺着移动方向推一把流体，带一点玫瑰色（同原型） */
  private stir(e: PointerEvent): void {
    const r = this.renderer.domElement.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width, y = 1 - (e.clientY - r.top) / r.height, p = this.pointer;
    if (p.has) {
      let dx = x - p.x, dy = y - p.y;
      const a = r.width / Math.max(1, r.height);
      if (a < 1) dx *= a;
      if (a > 1) dy /= a;
      if (dx || dy) {
        const sp = Math.min(1, Math.hypot(dx, dy) * 30), k = 0.06 * sp, c = this.dyeColor;
        c.setHSL((0.92 + 0.09 * Math.sin(this.time * 0.37)) % 1, 0.8, 0.55, THREE.SRGBColorSpace);
        this.fluid.splat(x, y, dx * FORCE * this.distort, dy * FORCE * this.distort, { r: c.r * k, g: c.g * k, b: c.b * k });
      }
    }
    p.x = x;
    p.y = y;
    p.has = true;
  }

  private ndc(e: PointerEvent): THREE.Vector2 {
    const r = this.renderer.domElement.getBoundingClientRect();
    return this.ndcV.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
  }

  /** 光环里点了一下：正面那张回到照片面板，侧面的转到正面，空白处荡开涟漪 */
  private tapCarousel(e: PointerEvent): void {
    const hit = this.carousel.pick(this.ndc(e), this.camera);
    if (hit?.front) this.carousel.callbacks?.onOpen();
    else if (hit) this.carousel.spinTo(hit.slot);
    else {
      const r = this.renderer.domElement.getBoundingClientRect();
      this.fluid.burst((e.clientX - r.left) / r.width, 1 - (e.clientY - r.top) / r.height, 0.8 * this.distort);
    }
  }

  /** 在某颗星的屏幕位置荡开一圈带它颜色的涟漪 */
  private burstAtStar(i: number, strength: number): void {
    const p = this.starPosition(i, this.tmp);
    if (!p) return;
    this.camera.updateMatrixWorld();
    p.project(this.camera);
    if (Math.abs(p.x) > 1.2 || Math.abs(p.y) > 1.2 || p.z > 1) return;
    const c = this.stars.colors, k = 0.05;
    this.fluid.burst((p.x + 1) / 2, (p.y + 1) / 2, strength * this.distort, { r: c[i * 3]! * k, g: c[i * 3 + 1]! * k, b: c[i * 3 + 2]! * k });
  }

  private resize(): void {
    const w = Math.max(1, this.container.clientWidth), h = Math.max(1, this.container.clientHeight);
    const dpr = Math.min(window.devicePixelRatio || 1, this.dprCap);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h);
    this.post.setSize(Math.round(w * dpr), Math.round(h * dpr));
    this.fluid.resize(w, h);
    this.camera.aspect = w / h;
    if (this.shift !== 0) this.camera.setViewOffset(w, h, 0, this.shift * h, w, h);
    else this.camera.updateProjectionMatrix();
    for (const layer of [this.stars, this.dust]) {
      layer.uniforms.uViewH.value = h * dpr;
      layer.uniforms.uMaxPx.value = 90 * dpr;
    }
    this.stars.uniforms.uMinPx.value = 2.4 * dpr;
    this.dust.uniforms.uMinPx.value = 1.6 * dpr;
    this.trail.uniforms.uViewH.value = h * dpr;
    this.trail.uniforms.uDpr.value = dpr;
    this.cocoons.uniforms.uViewH.value = h * dpr;
    this.cocoons.uniforms.uMinPx.value = 16 * dpr;
    this.constel.setResolution(w, h);
    this.rings.uniforms.uViewH.value = h * dpr;
    this.rings.uniforms.uMinPx.value = 12 * dpr;
    // 横竖屏换了：镜头重新对准光环正面
    if (this.carousel?.active) {
      const pose = this.carousel.cameraPose(this.camera.aspect, this.camera.fov);
      this.flyTo(pose.pos, pose.target, 0.5);
    }
  }

  /** 整体载入。reveal 时按日期从里往外依次点亮 */
  load(data: GalaxyData, reveal: boolean): void {
    this.space = data.space;
    this.placer = new StarPlacer(data.space.since);
    this.tints = new Map(data.members.map((m) => [m.id, m.tint]));
    this.shine = new Map(data.memories.map((m) => [m.id, m.shine]));
    this.titles = new Map(data.memories.flatMap((m) => (m.title ? [[m.id, m.title] as const] : [])));
    this.photos = [];
    this.index.clear();
    this.byDate.clear();
    this.gone.clear();
    this.crowdDirty.clear();
    this.grid.clear();
    this.maxSize = 0;
    this.pool.reset();
    this.outer = SPIRAL.r0;
    this.stars.reset(data.photos.length);
    this.select(-1, false);
    let lo = Number.POSITIVE_INFINITY, hi = Number.NEGATIVE_INFINITY;
    for (const p of data.photos) {
      const d = toDayNumber(p.date);
      lo = Math.min(lo, d);
      hi = Math.max(hi, d);
    }
    const t0 = this.time + 0.3, span = Math.max(1, hi - lo);
    for (const p of data.photos) {
      const order = (toDayNumber(p.date) - lo) / span;
      this.add(p, reveal ? t0 + 2.6 * order + hash01(p.id, 9) * 0.4 : -100, Math.min(1, order * 0.85 + hash01(p.id, 10) * 0.15));
    }
    this.rebuildGuide();
    this.layNebula();
    this.frameAll(false);
  }

  /** 加一颗星（已存在则返回原来的序号）；born 默认现在，会闪一下。order 是入场爆炸后落定的先后 */
  add(photo: PhotoDto, born = this.time, order = 1): number {
    const known = this.index.get(photo.id);
    if (known !== undefined) {
      // 从回收站恢复：在原来的位置重新亮起来（闪一下）
      if (this.gone.has(known)) this.revive(known);
      return known;
    }
    if (!this.placer) throw new Error('星海还没载入');
    const v = this.placer.place(photo, this.tmp);
    const i = this.photos.length;
    this.photos.push(photo);
    this.index.set(photo.id, i);
    this.grid.add(i, v.x, v.y, v.z);
    let day = this.byDate.get(photo.date);
    if (!day) this.byDate.set(photo.date, (day = []));
    day.push(i);
    this.crowdDirty.add(photo.date);
    // 这一天的照片多到可以显标题了，或者张数变了
    if (day.length >= TITLE_MIN) this.daysDirty = true;
    // 哪天有照片、有几张，都可能改变事件分组和雾的形状；跟回忆标题的节流一起重算
    this.nebulaDirty = true;
    // 比星轨现有的范围更早或更晚的照片：星轨要接长
    const range = this.trailRange;
    if (!range || photo.date < range[0] || photo.date > range[1]) this.guideDirty = true;
    const seed = hash01(photo.id, 5);
    const size = starSize(this.shine.get(photo.memoryId) ?? 3, seed);
    this.maxSize = Math.max(this.maxSize, size);
    // 照片在爱心上的位置由照片 id 决定，每次打开都一样
    const h = heartSample(Math.floor(hash01(photo.id, 11) * 4294967296), this.heart);
    this.stars.push({
      x: v.x,
      y: v.y,
      z: v.z,
      color: starColor(photo.color, this.tints.get(photo.createdBy), this.tmpColor),
      size,
      born,
      seed,
      heart: { x: h.x, y: h.y, z: h.z, kind: h.kind },
      burst: { x: h.bx, y: h.by, z: h.bz },
      order,
    });
    const r = Math.hypot(v.x, v.z);
    if (r > this.outer + 0.5) {
      this.outer = r;
      this.guideDirty = true;
    }
    return i;
  }

  /** 照片放进了回收站：星原地荡开一圈涟漪后熄灭。返回它的序号；不认识的照片返回 -1 */
  remove(id: string): number {
    const i = this.index.get(id);
    if (i === undefined) return -1;
    if (this.gone.has(i)) return i;
    this.burstAtStar(i, 0.8);
    this.gone.add(i);
    const p = this.stars.positions;
    this.grid.remove(i, p[i * 3]!, p[i * 3 + 1]!, p[i * 3 + 2]!);
    this.stars.setBorn(i, GONE);
    if (this.selected === i) this.ring.visible = false;
    this.daysDirty = true;
    this.nebulaDirty = true;
    this.crowdDirty.add(this.photos[i]!.date);
    return i;
  }

  private revive(i: number): void {
    this.gone.delete(i);
    const p = this.stars.positions;
    this.grid.add(i, p[i * 3]!, p[i * 3 + 1]!, p[i * 3 + 2]!);
    this.stars.setBorn(i, this.time);
    this.daysDirty = true;
    this.nebulaDirty = true;
    this.crowdDirty.add(this.photos[i]!.date);
  }

  /** 张数变了的日子：这一天每颗星记上现在亮着几颗 */
  private flushCrowd(): void {
    for (const date of this.crowdDirty) {
      const all = this.byDate.get(date) ?? [];
      const live = this.gone.size ? all.filter((k) => !this.gone.has(k)).length : all.length;
      for (const k of all) this.stars.setCrowd(k, Math.max(1, live));
    }
    this.crowdDirty.clear();
  }

  /** 这一天还有没有（没删掉的）照片：主题星座只把这样的日子算一站 */
  hasDay(date: LocalDate): boolean {
    return this.byDate.get(date)?.some((i) => !this.gone.has(i)) ?? false;
  }

  /** 这一天按添加顺序的第一张（没删掉的）；没有返回 -1 */
  firstOnDay(date: LocalDate): number {
    return this.byDate.get(date)?.find((i) => !this.gone.has(i)) ?? -1;
  }

  isGone(i: number): boolean {
    return this.gone.has(i);
  }

  /** 亮着的星有多少颗（放进回收站的不算） */
  get count(): number {
    return this.photos.length - this.gone.size;
  }

  /** 亮着的照片（存离线快照用） */
  livePhotos(): PhotoDto[] {
    return this.gone.size ? this.photos.filter((_, i) => !this.gone.has(i)) : this.photos;
  }

  /** 这段回忆里第一颗亮着的星；没有返回 -1 */
  firstOf(memoryId: string): number {
    for (let i = 0; i < this.photos.length; i++) if (this.photos[i]!.memoryId === memoryId && !this.gone.has(i)) return i;
    return -1;
  }

  /** 回忆来了或者改了：心动变了，这段回忆的星跟着变大变小；标题变了，回忆标题重算 */
  setMemory(m: MemoryDto): void {
    const shine = this.shine.get(m.id);
    this.shine.set(m.id, m.shine);
    if (shine !== undefined && shine !== m.shine) {
      for (let i = 0; i < this.photos.length; i++) {
        const p = this.photos[i]!;
        if (p.memoryId !== m.id) continue;
        const size = starSize(m.shine, hash01(p.id, 5));
        this.stars.setSize(i, size);
        this.maxSize = Math.max(this.maxSize, size);
      }
    }
    if ((this.titles.get(m.id) ?? null) === (m.title ?? null)) return;
    if (m.title) this.titles.set(m.id, m.title);
    else this.titles.delete(m.id);
    this.daysDirty = true;
  }

  /**
   * 在一起的日子改了：每颗星按新的螺线重新放。角度只看日期，所以每颗星都是沿着自己那天的射线滑到新的半径上。
   * 螺线、年份、星轨直接换成新的，等星落定了再淡入；滑动的这两秒里照片先隐去，也不能点选（photos.ts、pointerup）。
   * 镜头：开着照片就跟着那颗星过去，否则退到能看到整片新星海的位置。
   */
  setSince(since: LocalDate): void {
    if (!this.space || this.space.since === since) return;
    // 在一起的日子改了，整条螺线要挪：漫游走的旧路线没意义了
    this.exitRoam(false);
    this.space = { ...this.space, since };
    this.placer = new StarPlacer(since);
    this.grid.clear();
    this.outer = SPIRAL.r0;
    const n = this.photos.length, next = new Float32Array(n * 3), v = this.tmp;
    for (let i = 0; i < n; i++) {
      this.placer.place(this.photos[i]!, v);
      next.set([v.x, v.y, v.z], i * 3);
      if (!this.gone.has(i)) this.grid.add(i, v.x, v.y, v.z);
      this.outer = Math.max(this.outer, Math.hypot(v.x, v.z));
    }
    const t0 = this.time, spread = this.reducedMotion ? 0 : MOVE_SPREAD;
    for (let i = 0; i < n; i++) {
      const x = next[i * 3]!, y = next[i * 3 + 1]!, z = next[i * 3 + 2]!;
      this.stars.moveTo(i, x, y, z, t0 + spread * Math.min(1, Math.hypot(x, z) / this.outer), t0);
    }
    this.moveEnd = t0 + spread + this.stars.uniforms.uMove.value;
    this.layConstellation();
    this.layMarks();
    this.layNebula();
    this.rebuildGuide();
    const i = this.selected;
    if (i >= 0 && i < n) {
      const p = new THREE.Vector3().fromArray(next, i * 3), d = this.camera.position.distanceTo(this.controls.target);
      const dir = this.camera.position.clone().sub(this.controls.target).normalize();
      this.flyTo(p.clone().addScaledVector(dir, d), p, this.moveEnd - t0);
    } else this.frameAll(true);
  }

  /** 星落定以后螺线、年份、星轨、照片的淡入程度：没在滑时是 1，滑的时候是 0 */
  private moveVisibility(): number {
    return THREE.MathUtils.clamp((this.time - this.moveEnd) / MOVE_FADE, 0, 1);
  }

  /** 照片多的日子和它们的回忆标题（一天一段回忆，取那天第一张照片所属的回忆） */
  private dayInfo(): DayInfo[] {
    const out: DayInfo[] = [];
    for (const [date, all] of this.byDate) {
      const list = this.gone.size ? all.filter((i) => !this.gone.has(i)) : all;
      if (list.length < TITLE_MIN) continue;
      out.push({ date: date as LocalDate, count: list.length, title: this.titles.get(this.photos[list[0]!]!.memoryId) ?? null });
    }
    return out;
  }

  setMember(m: MemberDto): void {
    this.tints.set(m.id, m.tint);
  }

  /**
   * 时间胶囊（ADR-0011）：phase 是 0 静默封印、1 预热、2 等你拆、3 拆开过（capsule.ts 的 phaseOf）。
   * 星茧悬在解封那天的上方；那天如果还在将来，螺线的虚线会延伸过去
   */
  setCapsules(list: { id: string; date: LocalDate; phase: number }[]): void {
    const far = (l: { date: LocalDate }[]) => l.reduce<string>((m, c) => (c.date > m ? c.date : m), '');
    if (far(list) !== far(this.capsules)) this.guideDirty = true;
    this.capsules = list;
    this.layMarks();
  }

  /** 进入（或切换到）一个主题星座：dates 是各站，按时间先后（themes.ts 的 stationsOf）；镜头飞到装得下整个星座的位置 */
  enterConstellation(dates: readonly LocalDate[], name: string): void {
    this.exitRoam(false);
    if (!this.constelDates) this.constelPose = { pos: this.camera.position.clone(), target: this.controls.target.clone() };
    this.constelName = name;
    this.currentStation = null;
    this.setConstellation(dates);
    this.constelReveal = this.reducedMotion ? 1 : 0;
    const pose = this.constellationPose();
    if (pose) this.flyTo(pose.pos, pose.target, 1.4);
  }

  /** 正在看的星座的站变了（打了主题、加了照片、删了照片）：重画线和压暗，镜头不动 */
  updateConstellation(dates: readonly LocalDate[], name: string): void {
    if (!this.constelDates) return;
    this.constelName = name;
    this.setConstellation(dates);
  }

  /** 星座里现在看的那一站（那一圈环更亮，站名优先留下）；null 是没在看哪一站 */
  setCurrentStation(date: LocalDate | null): void {
    if (this.currentStation === date) return;
    this.currentStation = date;
    this.layConstellation();
  }

  /** 正在看的星座的各站（只显影这些日子的照片） */
  private get constelDays(): Set<LocalDate> | null {
    return this.constelDates ? (this.constelDaySet ??= new Set(this.constelDates)) : null;
  }
  private constelDaySet: Set<LocalDate> | null = null;

  /** 退出星座：线淡出、星亮回来；没在看照片时镜头飞回进入前的位置 */
  exitConstellation(): void {
    if (!this.constelDates) return;
    this.constelDates = null;
    this.constelDaySet = null;
    this.currentStation = null;
    const pose = this.constelPose;
    this.constelPose = null;
    if (pose && this.selected < 0 && !this.carousel.active) this.flyTo(pose.pos, pose.target, 1.2);
  }

  get inConstellation(): boolean {
    return this.constelDates !== null;
  }

  private setConstellation(dates: readonly LocalDate[]): void {
    this.constelDates = [...dates];
    this.constelDaySet = null;
    const on = new Set(dates);
    for (let i = 0; i < this.photos.length; i++) this.stars.setDim(i, on.has(this.photos[i]!.date) ? 0 : 1);
    this.layConstellation();
  }

  /** 各站：连线在那天的位置略抬高一点浮在星团上；每站一圈套住星团的细金环；语义缩放里的站名和主题名 */
  private layConstellation(): void {
    if (!this.space || !this.constelDates) return;
    const since = this.space.since, a = { x: 0, y: 0, z: 0 }, dates = this.constelDates;
    const points: THREE.Vector3[] = [], rings: RingInit[] = [];
    const stations = dates.map((d, k) => {
      dayAnchor(d, since, SPIRAL, a);
      points.push(new THREE.Vector3(a.x, a.y + 0.35, a.z));
      const day = (this.byDate.get(d) ?? []).filter((i) => !this.gone.has(i)), n = Math.max(1, day.length);
      // 星团向日葵铺开，半径约 0.17·√n（layout.ts 的 SPIRAL.cluster）；环套在外面一点
      rings.push({ x: a.x, y: a.y, z: a.z, radius: SPIRAL.cluster * Math.sqrt(n) + 0.3, first: k === 0, current: d === this.currentStation });
      // 站名：「3 · 厦门」，没写标题的用「5·20」
      const first = day[0], title = first !== undefined ? this.titles.get(this.photos[first]!.memoryId) : undefined;
      const { m, d: dd } = parseLocalDate(d);
      return { date: d, text: `${k + 1} · ${title ?? `${m}·${dd}`}`, count: n, current: d === this.currentStation };
    });
    this.constel.set(points);
    this.rings.set(rings);
    this.semantic.setConstellation({ name: this.constelName, stations });
    this.constelLabels = true;
  }

  /** 能装下整个星座的镜头位置：看着各站包围盒的中心，按默认仰角退到装得下的距离（至少 26 个单位，免得只有一两站时贴得太近） */
  private constellationPose(): CameraPose | null {
    if (!this.space || !this.constelDates?.length) return null;
    const since = this.space.since, a = { x: 0, y: 0, z: 0 }, box = new THREE.Box3();
    for (const d of this.constelDates) {
      dayAnchor(d, since, SPIRAL, a);
      box.expandByPoint(this.tmp.set(a.x, a.y, a.z));
    }
    const target = box.getCenter(new THREE.Vector3());
    const R = box.getSize(new THREE.Vector3()).length() / 2 + 4, halfV = THREE.MathUtils.degToRad(FOV / 2);
    const dist = Math.max(26, Math.max(R / Math.tan(halfV), R / (Math.tan(halfV) * this.camera.aspect)) * 1.15);
    return { pos: target.clone().add(new THREE.Vector3(0, Math.sin(ELEVATION) * dist, Math.cos(ELEVATION) * dist)), target };
  }

  /** 你们俩的生日（ADR-0011）：变了就重画语义缩放的射线和标签 */
  setBirthdays(list: { label: string; monthDay: string }[]): void {
    if (JSON.stringify(list) === JSON.stringify(this.birthdays)) return;
    this.birthdays = list;
    this.guideDirty = true;
  }

  /** 入场暗号的足迹（ADR-0011）：Ta 看过的那句落在留的那天 */
  setFootprints(list: { id: string; date: LocalDate }[]): void {
    this.footprints = list;
    this.layMarks();
  }

  /** 星茧和足迹（cocoons.ts 一层画完） */
  private layMarks(): void {
    if (!this.space) return;
    const since = this.space.since, a = { x: 0, y: 0, z: 0 };
    const marks: CocoonInit[] = this.capsules.map((c) => {
      dayAnchor(c.date, since, SPIRAL, a);
      return { id: c.id, x: a.x, y: a.y + 2.2, z: a.z, phase: c.phase, seed: hash01(c.id, 21) };
    });
    // 足迹浮在那天星团的外侧（离中心远的一边）；同一天有两个时沿螺线左右错开
    const perDay = new Map<string, number>(), placed = new Map<string, number>();
    for (const f of this.footprints) perDay.set(f.date, (perDay.get(f.date) ?? 0) + 1);
    for (const f of this.footprints) {
      dayAnchor(f.date, since, SPIRAL, a);
      const n = perDay.get(f.date)!, k = placed.get(f.date) ?? 0;
      placed.set(f.date, k + 1);
      const len = Math.hypot(a.x, a.z) || 1, ox = a.x / len, oz = a.z / len, side = (k - (n - 1) / 2) * 1.1;
      marks.push({ id: f.id, x: a.x + ox * 1.8 - oz * side, y: a.y + 0.5, z: a.z + oz * 1.8 + ox * side, phase: 4, seed: hash01(f.id, 23) });
    }
    this.cocoons.set(marks);
  }

  /** 事件星云（design §11.11）：相邻有照片的日子连成一件事，两天以上的罩一片淡雾；颜色和位置由那几天的星算 */
  private layNebula(): void {
    this.nebulaDirty = false;
    if (!this.space) return;
    const since = this.space.since, a = { x: 0, y: 0, z: 0 }, b = { x: 0, y: 0, z: 0 };
    const days: LocalDate[] = [];
    for (const [d, all] of this.byDate) if (all.some((i) => !this.gone.has(i))) days.push(d as LocalDate);
    const colors: number[] = [], patches: NebulaPatch[] = [];
    for (const ev of groupEvents(days)) {
      dayAnchor(ev[0]!, since, SPIRAL, a);
      dayAnchor(ev[ev.length - 1]!, since, SPIRAL, b);
      let cluster = 0;
      colors.length = 0;
      for (const d of ev) {
        const live = (this.byDate.get(d) ?? []).filter((i) => !this.gone.has(i));
        // 星团向日葵铺开，半径约 0.17·√n（同 layConstellation 的站环半径）
        cluster = Math.max(cluster, SPIRAL.cluster * Math.sqrt(live.length));
        for (const i of live) colors.push(this.stars.colors[i * 3]!, this.stars.colors[i * 3 + 1]!, this.stars.colors[i * 3 + 2]!);
      }
      patches.push(nebulaPatch(a, b, cluster, colors, hash01(ev[0]!, 29)));
    }
    this.nebula.set(patches);
  }

  /** 长按拆开的进度 0–1：星茧越来越亮 */
  pressCapsule(id: string, k: number): void {
    this.cocoons.setPress(id, k);
  }

  /** 星茧现在在屏幕上的位置（CSS 像素，相对于视口）；不在画面里时 null */
  capsuleScreenPosition(id: string): { x: number; y: number } | null {
    const p = this.cocoons.position(id, this.tmp);
    if (!p) return null;
    this.camera.updateMatrixWorld();
    p.project(this.camera);
    if (p.z > 1 || Math.abs(p.x) > 1.5 || Math.abs(p.y) > 1.5) return null;
    const r = this.renderer.domElement.getBoundingClientRect();
    return { x: r.left + ((p.x + 1) / 2) * r.width, y: r.top + ((1 - p.y) / 2) * r.height };
  }

  /** 镜头飞到一颗星茧跟前（提示里的「去看看」） */
  focusCapsule(id: string): void {
    this.exitRoam(false);
    const p = this.cocoons.position(id);
    if (!p) return;
    this.select(-1, false);
    const dir = this.camera.position.clone().sub(this.controls.target).normalize();
    this.flyTo(p.clone().addScaledVector(dir, 16), p, 1.3);
  }

  /** 星茧碎开：在它的位置荡开两圈冲击波，中心光晕亮一下 */
  shatterCapsule(id: string): void {
    this.coreFlare = Math.max(this.coreFlare, 0.8);
    const s = this.capsuleScreenPosition(id);
    if (!s || this.reducedMotion) return;
    const r = this.renderer.domElement.getBoundingClientRect();
    this.fluid.shockwave((s.x - r.left) / r.width, 1 - (s.y - r.top) / r.height);
  }

  /** 双星共鸣（两个人都写了这张照片，ADR-0011）：1 时星外面多两圈呼吸的光环；不认识的照片不管 */
  setMark(photoId: string, v: number): void {
    const i = this.index.get(photoId);
    if (i !== undefined) this.stars.setMark(i, v);
  }

  indexOf(id: string): number {
    return this.index.get(id) ?? -1;
  }

  /** 同一天的星，按添加顺序 */
  sameDay(i: number): number[] {
    const p = this.photos[i];
    if (!p) return [];
    const day = this.byDate.get(p.date) ?? [i];
    return this.gone.size ? day.filter((k) => !this.gone.has(k)) : day;
  }

  private rebuildGuide(): void {
    this.guideDirty = false;
    if (!this.space) return;
    const since = this.space.since;
    let first: LocalDate = since, lastDate: LocalDate = localDateOf(new Date());
    for (const d of this.byDate.keys()) {
      if (d < first) first = d as LocalDate;
      if (d > lastDate) lastDate = d as LocalDate;
    }
    // 时间胶囊在将来的日子上：虚线一直延伸到最远的那颗星茧后面
    for (const c of this.capsules) if (c.date > lastDate) lastDate = c.date;
    const end = addDays(lastDate, 30), days = daysBetween(first, end);
    // 照片还少的时候，也要把到今天为止的整条螺线放进画面
    this.outer = Math.max(this.outer, dayRadius(end, since));
    const pts = new Float32Array((Math.floor(days / 2) + 1) * 3);
    const a = { x: 0, y: 0, z: 0 };
    for (let k = 0, d = 0; d <= days; d += 2, k++) {
      dayAnchor(addDays(first, d), since, SPIRAL, a);
      pts.set([a.x, a.y, a.z], k * 3);
    }
    if (this.guide) {
      this.scene.remove(this.guide);
      this.guide.geometry.dispose();
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pts, 3));
    this.guide = new THREE.Line(g, new THREE.LineBasicMaterial({ color: '#8090ff', transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }));
    this.scene.add(this.guide);
    this.applyScenery();
    // 星轨从第一张照片走到最新一张，再虚线延到 end
    let photoFirst: string | null = null, photoLast: string | null = null;
    for (const d of this.byDate.keys()) {
      if (!photoFirst || d < photoFirst) photoFirst = d;
      if (!photoLast || d > photoLast) photoLast = d;
    }
    if (photoFirst && photoLast) {
      this.trail.rebuild(photoFirst as LocalDate, photoLast as LocalDate, end, since);
      this.trailRange = [photoFirst, photoLast];
    } else {
      this.trail.clear();
      this.trailRange = null;
    }
    this.semantic.rebuild({ since, first, end, hasPhotos: this.byDate.size > 0, days: this.dayInfo(), birthdays: this.birthdays });
    this.daysDirty = false;
  }

  /** 能看到整片星海的镜头位置 */
  overviewPose(): CameraPose {
    const R = this.outer + 6;
    const halfV = THREE.MathUtils.degToRad(FOV / 2);
    const fitV = R / Math.tan(halfV), fitH = R / (Math.tan(halfV) * this.camera.aspect);
    const dist = Math.max(fitV, fitH) * 1.05;
    return { pos: new THREE.Vector3(0, Math.sin(ELEVATION) * dist, Math.cos(ELEVATION) * dist), target: new THREE.Vector3() };
  }

  /**
   * Esc 退一层（design §3.2 的流程图）：一天 → 一年 → 整片星海。
   * 已经在看整片星海、正在入场或者在轮转里时返回 false。
   */
  stepBack(): boolean {
    if (this.introActive || this.carousel.active) return false;
    // Esc 退一层：漫游也算一层，退出来就是整片星海（exitRoam 里已经 frameAll 了）
    if (this.roam) {
      this.exitRoam(false);
      return true;
    }
    const target = this.controls.target, d = this.camera.position.distanceTo(target);
    if (d < DAY_LEVEL) {
      // 一天 → 一年：注视点不动，沿着现在的方向退到能看到一圈螺线的距离
      const dir = this.camera.position.clone().sub(target).normalize();
      this.flyTo(target.clone().addScaledVector(dir, YEAR_VIEW), target.clone(), 1.2);
      return true;
    }
    const home = this.overviewPose();
    if (d < home.pos.distanceTo(home.target) * 0.85 || target.distanceTo(home.target) > 1) {
      this.frameAll(true);
      return true;
    }
    return false;
  }

  /** 镜头退回能看到整片星海的位置 */
  frameAll(animated = true): void {
    const { pos, target } = this.overviewPose();
    this.controls.maxDistance = pos.length() * 2.5;
    if (animated) this.flyTo(pos, target, 1.4);
    else {
      this.camera.position.copy(pos);
      this.controls.target.copy(target);
      this.controls.update();
    }
  }

  /** 两层星点（装饰粒子、照片星） */
  get layers(): StarField[] {
    return [this.dust, this.stars];
  }

  /** 最后一道画面处理的参数（白光、撕裂、流体扭曲的强度） */
  get postUniforms(): ToneMapPass['uniforms'] {
    return this.post.uniforms;
  }

  /** 提前分配照片的纹理数组（入场爆炸的白光里调，见 PhotoPool.warm） */
  warmUp(): void {
    this.pool.warm();
  }

  /** 打开或关上星轨 */
  setTrail(on: boolean): void {
    this.trailOn = on;
  }

  /** 螺线、年份、中心光晕的可见度，0–1 */
  setScenery(k: number): void {
    this.scenery = k;
    this.applyScenery();
  }

  /** 轮转时螺线、年份淡出，中心光晕暗一半（同原型）；伪装时螺线、年份、标签都藏起来 */
  private applyScenery(): void {
    const ring = this.carousel.progress, k = this.scenery * (1 - ring) * this.moveShown * (this.disguised ? 0 : 1);
    this.coreOpacity = 0.7 * this.scenery * (1 - 0.5 * ring);
    // 星茧跟着螺线一起：入场、轮转、伪装时藏起来
    this.marksK = k;
    this.cocoons.uniforms.uOpacity.value = k * (1 - 0.4 * this.constelK);
    if (this.guide) (this.guide.material as THREE.LineBasicMaterial).opacity = 0.1 * k;
    this.labelLayer.style.opacity = String(k);
  }

  /**
   * 轮转这一天的照片：stars 按添加顺序，start 是正面那张在里面的位置。
   * 光环悬在星团上方、正面朝着镜头现在的方向；镜头飞过去，手势交给光环。
   */
  enterCarousel(stars: number[], start: number, hooks: CarouselHooks): void {
    if (this.introActive || stars.length < 2) return;
    this.exitRoam(false);
    this.select(-1, false);
    const center = new THREE.Vector3(), p = new THREE.Vector3();
    for (const i of stars) if (this.starPosition(i, p)) center.add(p);
    center.divideScalar(stars.length);
    const aspects = stars.map((i) => {
      const ph = this.photos[i];
      return ph?.width && ph.height ? ph.width / ph.height : 4 / 3;
    });
    this.carousel.enter({ stars, start, aspects, center, facing: this.camera.position, hooks });
    this.controls.enabled = false;
    this.controls.autoRotate = false;
    const pose = this.carousel.cameraPose(this.camera.aspect, this.camera.fov);
    this.flyTo(pose.pos, pose.target, 1.9);
  }

  /** 收起光环，照片飞回各自的星；返回正面那张星的序号 */
  leaveCarousel(): number {
    const front = this.carousel.front;
    this.carousel.leave();
    this.controls.enabled = true;
    this.renderer.domElement.style.cursor = '';
    return front;
  }

  /** 光环展开着（收起的动画开始后就不算了） */
  get inCarousel(): boolean {
    return this.carousel.active;
  }

  /** 光环转一格 */
  carouselStep(d: number): void {
    if (this.carousel.active) this.carousel.step(d);
  }

  /**
   * 日常、纪念日入场（ritual.ts）：照片星从中心的一个光点里绽开，dur 秒落定。
   * 借用入场爆炸的那套着色器（stars.ts 的 uBang）：爱心缩成中心的光点，往外冲的那一下也收小；
   * 背景天幕不动，镜头、手势照常。减少动态效果时不绽开。
   */
  bloom(dur: number): void {
    this.coreFlare = Math.max(this.coreFlare, 1);
    if (this.reducedMotion || dur <= 0) return;
    const u = this.stars.uniforms;
    // 中心一颗约 10 个单位宽的小心形（在中心光晕里面）
    u.uHeartSize.value = 5;
    u.uBurstK.value = 8;
    // 所有照片星挤在小心形里会叠很多层（1 万颗约 30 层），按张数压低亮度，不然开头一下是一大团刺眼的光
    u.uHeartGain.value = Math.min(1.3, 2.5 / (1 + this.count * 0.003));
    u.uBang.value = 0;
    this.blooming = { t0: this.time, dur };
  }

  /** 绽开的进度；落定以后把爱心的尺寸、往外冲的幅度换回入场爆炸用的 */
  private stepBloom(): void {
    const b = this.blooming!, u = this.stars.uniforms;
    const k = Math.min(1, (this.time - b.t0) / b.dur);
    u.uBang.value = 1 - Math.pow(1 - k, 3);
    if (k < 1) return;
    this.blooming = null;
    u.uBang.value = 1;
    u.uHeartSize.value = 16;
    u.uBurstK.value = 115;
    u.uHeartGain.value = 1.3;
  }

  /** 纪念日入场的极光：k 是强度（0 不画），rise 是升起的程度，都是 0–1 */
  setAurora(k: number, rise: number): void {
    this.auroraK = this.reducedMotion ? Math.min(k, 0.6) : k;
    this.auroraRise = rise;
  }

  /**
   * Ta 赴约的回响（2026-10-02 改造需求「接力点亮」的发起方那一侧）：中心光晕亮一下，从中心荡开两圈流体冲击波。
   * 只是一阵光，不动镜头、不挡操作。
   */
  echo(): void {
    this.coreFlare = Math.max(this.coreFlare, 1.6);
    if (this.reducedMotion) return;
    this.camera.updateMatrixWorld();
    const c = this.tmp.set(0, 0, 0).project(this.camera);
    if (Math.abs(c.x) <= 1.2 && Math.abs(c.y) <= 1.2 && c.z <= 1) this.fluid.shockwave((c.x + 1) / 2, (c.y + 1) / 2);
  }

  /** 防借看（2026-10-02 改造需求「一键防尴尬伪装」）：只留星点、天幕和流星慢慢转，像一张深空动态壁纸 */
  setDisguise(on: boolean): void {
    if (this.disguised === on) return;
    this.disguised = on;
    if (on) {
      // 退到整片星海慢慢转：近看的星团还是看得出一张张照片的颜色
      this.exitRoam(false);
      this.select(-1, false);
      this.frameAll(true);
      this.controls.autoRotate = !this.reducedMotion;
    }
    this.applyScenery();
  }

  get isDisguised(): boolean {
    return this.disguised;
  }

  /** 入场开始：镜头和两层星点交给入场动画，不能点选 */
  beginIntro(): void {
    this.exitRoam(false);
    this.introActive = true;
    this.flight = null;
    this.controls.enabled = false;
    this.controls.autoRotate = false;
    this.select(-1, false);
    this.setScenery(0);
    for (const layer of this.layers) layer.uniforms.uBang.value = 0;
  }

  /** 入场结束：镜头停在 pose，交还给手势控制 */
  endIntro(pose: CameraPose): void {
    this.introActive = false;
    for (const layer of this.layers) {
      layer.uniforms.uBang.value = 1;
      layer.uniforms.uOpacity.value = 1;
    }
    this.camera.position.copy(pose.pos);
    this.controls.target.copy(pose.target);
    this.controls.maxDistance = pose.pos.distanceTo(pose.target) * 2.5;
    this.controls.enabled = true;
    this.controls.autoRotate = !this.reducedMotion;
    this.controls.update();
    this.setScenery(1);
  }

  /**
   * 开始时光漫游（design §11.11）：镜头交给漫游时间轴沿星轨走，彗星由漫游推着走，只显影停靠站那天的照片。
   * stops 按日期排好，shines 和它一一对应。星轨没有实线段（有照片的日子少于两天）、stops 为空、正在入场时返回 false。
   * opts.constellation 是星座里的漫游：时间轴从第一站开始、站间沿金线直飞（hop 固定 1）、停完最后一站没有收尾段；
   * 彗星藏起来（金线是向导，彗星在螺线上跑会和金线打架）；结束时镜头飞回装得下整个星座的位置而不是全景（exitRoam）。
   */
  startRoam(stops: readonly LocalDate[], shines: readonly number[], hooks: RoamHooks, opts?: { constellation?: boolean }): boolean {
    const path = this.trail.path;
    if (this.introActive || this.roam || !path || path.sLast <= 0.5 || !stops.length) return false;
    this.flight = null;
    this.controls.enabled = false;
    this.controls.autoRotate = false;
    const constel = opts?.constellation === true;
    const arcs = stops.map((d) => arcOf(path, d));
    const tl = roamTimeline(arcs, shines, path.sLast, path.total, {
      path,
      from: constel ? (arcs[0] ?? 0) : 0,
      direct: constel,
      end: !constel,
      still: this.reducedMotion,
    });
    this.roam = {
      path,
      tl,
      days: new Set(stops),
      constel,
      hooks,
      t: 0,
      speed: 1,
      paused: false,
      leg: 0,
      stop: -1,
      // 开场段（1.4 秒）里镜头从现在的位置往漫游镜头混，飞完正好接上，接缝不跳；减少动态效果时不混，直接切
      blend: this.reducedMotion ? null : { pos: this.camera.position.clone(), target: this.controls.target.clone(), t: 0, dur: tl.legs[0]!.t1 - tl.legs[0]!.t0 },
    };
    // 星座漫游里彗星钉在 2：头在实线、虚线上都没有亮光（trail 的着色器里，aT−2 太远、拖尾也够不着）
    this.trail.setComet(constel ? 2 : 0);
    return true;
  }

  /** 退出漫游：交还镜头，彗星还给自己循环，星轨回到用户的开关，镜头飞回整片星海 */
  stopRoam(): void {
    this.exitRoam(false);
  }

  private exitRoam(finished: boolean): void {
    const r = this.roam;
    if (!r) return;
    this.roam = null;
    this.controls.enabled = true;
    this.trail.setComet(null);
    // 显影的日子、星轨的亮灭都跟着 this.roam 为空自动还原；不用恢复自转（跟看照片回来一样停在转停的状态）
    // 星座里的漫游结束时星座还开着：镜头飞回装得下整个星座的位置；漫游中星座被退出了（Ta 删了这个主题）就回全景
    const pose = r.constel && this.constelDates?.length ? this.constellationPose() : null;
    if (pose) this.flyTo(pose.pos, pose.target, 1.4);
    else this.frameAll(true);
    r.hooks.onStop(finished);
  }

  pauseRoam(paused: boolean): void {
    if (this.roam) this.roam.paused = paused;
  }

  /** 快进时 2.5，平时 1 */
  setRoamSpeed(scale: number): void {
    if (this.roam) this.roam.speed = scale;
  }

  /** 上一段 / 下一段：跳到第 stop 站到站的那一刻（夹到 0..站数−1）；镜头从现在的位置混过去 */
  seekRoam(stop: number): void {
    const r = this.roam;
    if (!r) return;
    const i = THREE.MathUtils.clamp(Math.round(stop), 0, r.tl.stops.length - 1);
    const leg = r.tl.legs.findIndex((l) => l.kind === 'stop' && l.stop === i);
    if (leg < 0) return;
    // 减少动态效果时不混，镜头直接切到那一站的机位
    r.blend = this.reducedMotion ? null : { pos: this.camera.position.clone(), target: this.controls.target.clone(), t: 0, dur: ROAM_SEEK_BLEND };
    r.t = r.tl.legs[leg]!.t0;
    this.roamLegTo(leg);
  }

  get roaming(): boolean {
    return this.roam !== null;
  }

  /** 给下一段的界面用：stop 是现在停着的或者最近到过的那站，还没到第一站是 -1 */
  get roamState(): { t: number; total: number; stop: number; stops: number; atStop: boolean; paused: boolean } | null {
    const r = this.roam;
    if (!r) return null;
    return { t: r.t, total: r.tl.total, stop: r.stop, stops: r.tl.stops.length, atStop: r.tl.legs[r.leg]?.kind === 'stop', paused: r.paused };
  }

  /** 漫游进入另一段：离开的是站就调 onDepart，到达的是站就调 onArrive；被整段跳过的站不算到过 */
  private roamLegTo(leg: number): void {
    const r = this.roam!;
    if (leg === r.leg) return;
    const old = r.tl.legs[r.leg]!, now = r.tl.legs[leg]!;
    if (old.kind === 'stop') r.hooks.onDepart(old.stop);
    r.leg = leg;
    if (now.kind === 'stop') {
      r.stop = now.stop;
      r.hooks.onArrive(now.stop);
    }
  }

  /** 漫游每帧：时钟推进（暂停不走、快进加速），镜头沿路线摆，彗星跟着，到站/离站回调，走完自己退出 */
  private stepRoam(dt: number): void {
    const r = this.roam!;
    if (!r.paused) r.t = Math.min(r.tl.total, r.t + dt * r.speed);
    const at = roamAt(r.tl, r.t);
    this.roamLegTo(at.leg);
    // 彗星换算用星轨现在的 sLast：漫游中星轨重建了，镜头照旧走完旧路线，彗星也不和镜头错开。
    // 星座漫游里彗星一直钉在 2（不亮）：金线是向导，彗星在螺线上跑会和金线打架
    this.trail.setComet(r.constel ? 2 : at.s / Math.max(1e-3, this.trail.path?.sLast ?? r.path.sLast));
    roamCamera(r.path, r.tl, r.t, this.roamCam);
    const b = r.blend;
    if (b) {
      // 开始、seek 时从上一个镜头位置往漫游镜头混（漫游暂停时也照常混，镜头不僵住）
      b.t = Math.min(b.dur, b.t + dt);
      const e = easeInOut(b.t / b.dur);
      this.camera.position.lerpVectors(b.pos, this.roamCam.pos, e);
      this.controls.target.lerpVectors(b.target, this.roamCam.target, e);
      if (b.t >= b.dur) r.blend = null;
    } else {
      this.camera.position.copy(this.roamCam.pos);
      this.controls.target.copy(this.roamCam.target);
    }
    // 每帧报位置（暂停时 r.t 不变也报）：界面的进度线跟着刷
    r.hooks.onTick(r.t, r.tl.total);
    // 走完收尾段：自己退出（同 stopRoam，onStop 是 true）
    if (r.t >= r.tl.total) this.exitRoam(true);
  }

  /**
   * 俯仰限位（MAX_POLAR）。OrbitControls 每次 update 都按上限夹角度，程序放的镜头也会被夹，
   * 所以飞行、轮转时不限；程序把镜头放到限位以下（比如从轮转回到照片），上限就从那个角度起，只会往限位收：
   * 往上抬可以，往下压不行，也不会突然跳一下。
   */
  private limitPitch(): void {
    const c = this.controls;
    if (this.flight || this.carousel.active || this.roam) {
      c.maxPolarAngle = Math.PI;
      return;
    }
    const off = this.tmp.copy(this.camera.position).sub(c.target);
    const phi = Math.acos(THREE.MathUtils.clamp(off.y / Math.max(1e-6, off.length()), -1, 1));
    c.maxPolarAngle = Math.max(MAX_POLAR, Math.min(c.maxPolarAngle, phi + 1e-4));
  }

  private flyTo(pos: THREE.Vector3, target: THREE.Vector3, dur: number): void {
    // 漫游管着镜头：别的飞行一律不生效（要动镜头的地方会先 exitRoam）
    if (this.roam) return;
    this.flight = {
      fromPos: this.camera.position.clone(),
      fromTarget: this.controls.target.clone(),
      toPos: pos.clone(),
      toTarget: target.clone(),
      t0: this.time,
      dur: this.reducedMotion ? 0.01 : dur,
    };
  }

  /** 镜头飞到某颗星跟前，保持当前的观察方向 */
  focus(i: number, distance = 7): void {
    if (i < 0 || i >= this.stars.count) return;
    // 星还在滑的话，飞到它要落定的地方
    const p = new THREE.Vector3().fromArray(this.stars.positions, i * 3);
    const dir = this.camera.position.clone().sub(this.controls.target).normalize();
    this.flyTo(p.clone().addScaledVector(dir, distance), p, 1.3);
  }

  /** 某颗星现在在屏幕上的位置（CSS 像素，相对于视口）；在镜头背后或者画面外很远时返回 null */
  screenPosition(i: number): { x: number; y: number } | null {
    const p = this.starPosition(i, this.tmp);
    if (!p) return null;
    this.camera.updateMatrixWorld();
    p.project(this.camera);
    if (p.z > 1 || Math.abs(p.x) > 1.5 || Math.abs(p.y) > 1.5) return null;
    const r = this.renderer.domElement.getBoundingClientRect();
    return { x: r.left + ((p.x + 1) / 2) * r.width, y: r.top + ((1 - p.y) / 2) * r.height };
  }

  /** 星现在画在哪（滑向新螺线的途中是半路上的位置） */
  starPosition(i: number, out = new THREE.Vector3()): THREE.Vector3 | null {
    if (i < 0 || i >= this.stars.count) return null;
    return this.stars.displayed(i, this.time, out);
  }

  select(i: number, fly = true): void {
    // 漫游中点星会接管镜头：先退出漫游（flyTo 盖过 exitRoam 里的 frameAll）
    if (i >= 0) this.exitRoam(false);
    // 放进回收站的星选不了（比如点了一条旧提示里的「去看看」）
    const p = i >= 0 && !this.gone.has(i) ? this.starPosition(i) : null;
    this.selected = p ? i : -1;
    this.ring.visible = p !== null;
    if (p) {
      this.ring.position.copy(p);
      this.controls.autoRotate = false;
      if (fly) {
        this.burstAtStar(i, 1);
        this.focus(i);
      }
    }
    this.onSelect?.(p ? i : -1);
  }

  get selectedIndex(): number {
    return this.selected;
  }

  /** -0.5 到 0.5：画面中心竖直移屏幕高度的这个比例；正数是上移（底部面板），负数是下移（漫游时宽屏的卡片占着上半，星团让到卡片下面） */
  setViewShift(fraction: number): void {
    this.shiftTarget = THREE.MathUtils.clamp(fraction, -0.5, 0.5);
  }

  /** 屏幕坐标下最近的一颗星；没有返回 -1。用空间网格只查点击射线附近的星（pick.ts） */
  pickAt(clientX: number, clientY: number): number {
    const rect = this.renderer.domElement.getBoundingClientRect();
    return pickStar({
      camera: this.camera,
      x: clientX - rect.left,
      y: clientY - rect.top,
      width: rect.width,
      height: rect.height,
      positions: this.stars.positions,
      sizes: this.stars.sizes,
      borns: this.stars.borns,
      count: this.stars.count,
      time: this.time,
      grid: this.grid,
      maxSize: this.maxSize,
    });
  }

  private frame(): void {
    const now = performance.now();
    // 画质：按帧间隔自动换档（页面在后台时浏览器不调这里，回来的第一帧间隔很长，那种不算）
    const next = this.governor.sample(now - this.last, now);
    if (next !== null) {
      this.applyQuality(next);
      this.onQuality?.(next);
    }
    const dt = Math.min(0.1, (now - this.last) / 1000);
    this.stats.frameMs += (now - this.last - this.stats.frameMs) * 0.05;
    this.stats.fps = 1000 / Math.max(1, this.stats.frameMs);
    this.last = now;
    this.time += dt;

    const f = this.flight;
    if (f && !this.introActive) {
      const k = Math.min(1, (this.time - f.t0) / f.dur), e = easeInOut(k);
      this.camera.position.lerpVectors(f.fromPos, f.toPos, e);
      this.controls.target.lerpVectors(f.fromTarget, f.toTarget, e);
      if (k >= 1) this.flight = null;
    } else if (this.roam && !this.introActive) {
      this.stepRoam(dt);
    }
    if (!this.introActive) {
      this.limitPitch();
      this.controls.update(dt);
    }
    this.onFrame?.(dt, this.time);
    if (this.shift !== this.shiftTarget) {
      this.shift += (this.shiftTarget - this.shift) * (this.reducedMotion ? 1 : 1 - Math.exp(-dt * 6));
      if (Math.abs(this.shift - this.shiftTarget) < 1e-4) this.shift = this.shiftTarget;
      const w = this.container.clientWidth, h = this.container.clientHeight;
      if (this.shift !== 0) this.camera.setViewOffset(w, h, 0, this.shift * h, w, h);
      else this.camera.clearViewOffset();
    }

    // 中心的心：很轻的“咚—咚”；绽开、Ta 赴约时亮一下再慢慢退
    const ph = (this.time % 1.3) / 1.3;
    const beat = this.reducedMotion ? 0 : Math.exp(-(((ph - 0.05) / 0.05) ** 2)) + 0.55 * Math.exp(-(((ph - 0.28) / 0.06) ** 2));
    this.coreFlare = this.coreFlare > 0.002 ? this.coreFlare * Math.exp(-dt * 1.6) : 0;
    this.core.scale.setScalar(SPIRAL.r0 * (1.1 + beat * 0.06) * (1 + 0.3 * this.coreFlare));
    (this.core.material as THREE.SpriteMaterial).opacity = Math.min(1, this.coreOpacity * (1 + 0.45 * this.coreFlare));
    this.aurora.set(this.auroraK, this.auroraRise, this.time, this.camera.aspect);
    this.cocoons.uniforms.uTime.value = this.time;
    (this.ring.material as THREE.SpriteMaterial).opacity = 0.75 + 0.25 * Math.sin(this.time * 3);
    this.ring.scale.setScalar(0.045 + 0.004 * Math.sin(this.time * 3));

    // 星在滑向新螺线：选中的圈跟着星走；落定以后螺线、年份这些淡入
    if (this.time < this.moveEnd && this.selected >= 0) this.starPosition(this.selected, this.ring.position);
    const settled = this.moveVisibility();
    if (settled !== this.moveShown) {
      this.moveShown = settled;
      this.applyScenery();
    }

    if (this.guideDirty) this.rebuildGuide();
    // 加照片很快时（一次传几百张），回忆标题、事件星云最多一秒重算一次
    else if ((this.daysDirty || this.nebulaDirty) && this.time - this.daysAt > 1) {
      this.daysDirty = false;
      this.daysAt = this.time;
      this.semantic.setDays(this.dayInfo());
      if (this.nebulaDirty) this.layNebula();
    }
    // 轮转：卡片在星和光环之间飞；星海暗到一半，螺线、年份、星轨淡出（同原型）
    this.carousel.update(dt, this.camera, (i, out) => this.starPosition(i, out), (i, out) => out.fromArray(this.stars.colors, i * 3));
    const ring = this.carousel.progress;
    if (!this.introActive) for (const layer of this.layers) layer.uniforms.uOpacity.value = 1 - 0.5 * ring;
    // 没有入场动画时，打开 1.5 秒后预热照片的纹理数组
    if (!this.introActive && this.time > 1.5) this.pool.warm();
    // 照片面片：入场时星还在爱心上，不显影；轮转时让位给光环；伪装时不显影
    // 主题星座里只显影主题里的日子（不在里面的星暗着，照片也别冒出来）；漫游时只显影停靠站那天，漫游优先
    const onDays = this.constelDays;
    const roamDays = this.roam?.days;
    this.pool.update({
      time: this.time,
      camera: this.camera,
      viewH: this.container.clientHeight,
      stars: this.stars,
      photos: this.photos,
      opacity: this.introActive || this.disguised ? 0 : this.scenery * (1 - ring) * settled,
      only: roamDays ? (i) => roamDays.has(this.photos[i]!.date) : onDays ? (i) => onDays.has(this.photos[i]!.date) : undefined,
    });
    if (ring !== this.ringShown) {
      this.ringShown = ring;
      this.applyScenery();
    }
    // 星轨：入场时随螺线一起从第一段回忆画到最后；看照片时淡一些（同原型的聚焦模式）；漫游时一定亮
    this.trailK += (((this.trailOn || this.roam !== null) && !this.disguised ? (this.selected >= 0 ? 0.45 : 1) : 0) - this.trailK) * (1 - Math.exp(-3 * dt));
    const tu = this.trail.uniforms;
    // 主题星座：线和压暗跟着进出平滑过渡（约 0.9 秒），进入时线从第一站往最后一站画；星轨让到四成
    const inConstel = this.constelDates !== null, ease = this.reducedMotion ? 1 : 1 - Math.exp(-dt / 0.3);
    this.constelK += ((inConstel ? 1 : 0) - this.constelK) * ease;
    this.roamK += ((this.roam ? 1 : 0) - this.roamK) * ease;
    this.dimK += ((inConstel ? 1 : 0) - this.dimK) * ease;
    this.stars.uniforms.uDimK.value = this.dimK;
    if (inConstel && this.constelReveal < 1) this.constelReveal = Math.min(1, this.constelReveal + dt / 1.2);
    if (!this.reducedMotion) this.constelFlow += dt * ConstellationLines.flowSpeed;
    const constelShown = this.constelK * this.scenery * (1 - ring) * settled * (this.disguised ? 0 : 1);
    this.constel.update(constelShown, this.constelReveal, this.constelFlow);
    // 漫游时站环淡到两成：停站时镜头离站很近，金环又大又亮，淡一半照样压住底部控制条的字
    this.rings.uniforms.uOpacity.value = constelShown * (1 - 0.8 * this.roamK);
    this.rings.uniforms.uTime.value = this.reducedMotion ? 0 : this.time;
    // 星茧、足迹在星座里稍微退后（淡到六成）
    this.cocoons.uniforms.uOpacity.value = this.marksK * (1 - 0.4 * this.constelK);
    // 事件星云：跟星茧同一套可见度（伪装、入场、轮转、星没落定都跟着隐去），星座里 ×0.4；
    // 再按镜头远近分层，看照片时（bloomK）淡掉
    this.nebula.update(
      this.reducedMotion ? 0 : this.time,
      nebulaZoom(this.camera.position.distanceTo(this.controls.target)) * (1 - 0.6 * this.constelK) * (1 - this.bloomK) * this.marksK,
    );
    // 退出星座：线淡完了再清掉语义缩放里的站名和主题名
    if (!inConstel && this.constelLabels && this.constelK < 0.02) {
      this.constelLabels = false;
      this.semantic.setConstellation(null);
    }
    tu.uOpacity.value = this.trailK * (1 - ring) * settled * (1 - 0.6 * this.constelK);
    tu.uDraw.value = this.scenery * (tu.uEnd.value + 0.01);
    tu.uTime.value = this.time;
    this.meteors.update(dt, this.time, this.camera, !this.reducedMotion && !this.introActive && !this.roam && this.selected < 0 && ring === 0);
    if (this.crowdDirty.size) this.flushCrowd();
    if (this.blooming) this.stepBloom();
    for (const layer of this.layers) {
      layer.uniforms.uTime.value = this.time;
      layer.commit();
    }
    this.fluid.step(dt);
    const u = this.post.uniforms;
    u.tVel.value = this.fluid.velocityTexture;
    u.tDye.value = this.fluid.dyeTexture;
    u.uTime.value = this.time;
    // 辉光：看照片时变弱、阈值变高，免得抢照片的戏；入场白光时一起冲高（同原型）
    this.bloomK += ((this.selected >= 0 ? 1 : 0) - this.bloomK) * (1 - Math.exp(-2.5 * dt));
    const bloom = this.post.bloom, k = this.bloomK, intro = 1 - this.scenery, L = this.bloomLook, lerp = THREE.MathUtils.lerp;
    const look = (v: number[]) => lerp(lerp(v[0]!, v[1]!, k), v[2]!, intro);
    // 画质降到关辉光那一档时，强度 0 就整个跳过（post.ts）
    bloom.strength = this.bloomOn ? look(L.strength) + u.uFlash.value * 1.4 : 0;
    bloom.threshold = look(L.threshold);
    bloom.radius = look(L.radius);
    // 「绘制次数」只数场景本身，看的是星点有没有合批；流体、辉光、最后合成的那些遍不算
    this.renderer.info.reset();
    const overlay = this.carousel.visible || this.pool.visible || this.ring.visible;
    this.post.render(this.renderer, this.scene, this.camera, overlay ? this.overlay : null);
    this.stats.calls = this.post.sceneCalls;
    this.stats.stars = this.stars.count;
    this.semantic.update({
      camera: this.camera,
      target: this.controls.target,
      width: this.container.clientWidth,
      height: this.container.clientHeight,
      k: this.disguised ? 0 : this.scenery * (1 - ring) * settled * (1 - 0.7 * this.roamK),
      constel: this.constelK,
    });
  }
}
